import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, router } from '@inertiajs/react'
import * as z from 'zod'
import { useTranslation } from 'react-i18next'
import { OrganizationLayout } from '../../layouts/organization-layout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '../../components/ui/form'

type OrganizationForm = {
  name: string
  description?: string
}

interface Organization {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
}

interface OrganizationEditProps {
  organization: Organization
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Edit({ organization }: OrganizationEditProps) {
  const { t } = useTranslation('organizations')

  const organizationSchema = z.object({
    name: z.string().min(2, t('edit.validation.nameMin')).max(100, t('edit.validation.nameMax')),
    description: z.string().optional(),
  })

  const form = useForm<OrganizationForm>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: organization.name,
      description: organization.description || '',
    },
  })

  const onSubmit = (data: OrganizationForm) => {
    router.patch('/organization', { organization: data })
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <div className="mx-auto max-w-4xl space-y-8">

            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t('edit.details.title')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('edit.details.subtitle')}</p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('edit.form.name.label')} <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder={t('edit.form.name.placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('edit.form.name.description')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('edit.form.description.label')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('edit.form.description.placeholder')}
                          className="min-h-[120px] resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('edit.form.description.description')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center justify-end gap-3">
                  <Button asChild variant="outline" className="max-sm:w-full">
                    <Link href="/organization">{t('actions.cancel')}</Link>
                  </Button>
                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting}
                    className="max-sm:w-full"
                  >
                    {form.formState.isSubmitting ? t('edit.actions.saving') : t('edit.actions.save')}
                  </Button>
                </div>
              </form>
            </Form>

          </div>
        </div>
      </div>
    </div>
  )
}

Edit.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default Edit
